import React, { useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styles from './styles.module.css';
import Text from 'components/shared/Text';
import MobileContext from 'context/MobileContext';
import { Formik } from 'formik';
import { freezeFormSchema } from 'validation/auth.validation';
import Textfield from 'components/shared/Textfield';
import TextArea from 'components/shared/TextArea';
import CustomDropdown from 'components/shared/SelectState';
import { Dialog, makeStyles } from '@material-ui/core';
import { checkmarkCircleOutline, infoIcon } from 'assets/newIcons';
import { CircularProgress } from '@mui/material';
import history from 'store/history';
import SingleDateSelection from 'components/shared/SingleDateSelection';

const initialValues = {
  email: '',
  duration: '',
  freezeDate: '',
  notes: '',
};

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    borderRadius: '12px',
  },
}));

const FreezeMembership = ({
  loadFreezeDropdownData,
  freezeDropdownDetails,
  submitFreezeForm,
  isSubmitFreezeLoading,
  freezeFormDetails,
  resetFreezeFormDetails,
}) => {
  const isMobile = useContext(MobileContext);
  const userDetails = useSelector(state => state?.auth?.profile);
  const userFirstName = useSelector(state => state?.auth?.user?.firstName);
  const userPhoneNumber = userDetails?.phone || userDetails?.mobilephone;
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const [openThanksModal, setOpenThanksModal] = useState(false);
  const [onFreezeForm, setOnFreezeForm] = useState(false);
  const [freezeDate, setFreezeDate] = useState(null);
  const [duration, setDuration] = useState('');
  const [notes, setNotes] = useState('');
  const validData = !!(duration && freezeDate && notes.trim());
  const classes = useStyles();
  const boxShadow = '0px 4px 12px rgba(0, 0, 0, 0.08)';
  const inputWidth = isMobile ? '90.41vw' : '59.5vw';
  const durationOptions = freezeDropdownDetails?.dropDownData;
  const agreementText = freezeDropdownDetails?.agreementText;
  const freezeFormData = useSelector(state => state.membershipForms.formsDetails);
  const path = window.location.pathname;

  const formatDate = date => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    return formattedDate;
  };

  const formattedFreezeDate = freezeDate ? formatDate(freezeDate) : '';

  useEffect(() => {
    if (path === '/membership-form/freeze' && Object.keys(freezeFormData).length === 0) {
      history.push('/membership-form');
    }
  }, [freezeFormData, path]);

  useEffect(() => {
    if (Object.keys(freezeDropdownDetails).length === 0) {
      loadFreezeDropdownData();
    }
  }, []);

  useEffect(() => {
    const isFormSubmittedFromStorage =
      localStorage.getItem('freezeFormSubmitted') === 'true';
    if ((onFreezeForm || isFormSubmittedFromStorage) && freezeFormDetails?.success) {
      setOpenThanksModal(true);
    }
  }, [freezeFormDetails]);

  const handleDuration = e => {
    setDuration(e);
  };

  const handleAddNotes = e => {
    setNotes(e);
  };

  const handleInfoClick = () => {
    setOpenInfoModal(true);
  };

  const handleCloseInfoModal = () => {
    setOpenInfoModal(false);
  };

  const handleGoToBackClick = () => {
    setOpenThanksModal(false);
    setOnFreezeForm(false);
    localStorage.removeItem('freezeFormSubmitted');
    resetFreezeFormDetails();
    history.push('/');
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={values => {
          const { email: optionalEmail, notes, duration } = values;
          const { email, lastName } = userDetails;
          const data = {
            firstName: userFirstName,
            lastName,
            phoneNumber: userPhoneNumber,
            email: email,
            alternateEmail: optionalEmail,
            freezeStartDate: formattedFreezeDate,
            duration,
            notes: notes.trim(),
            isPoral: true,
          };
          submitFreezeForm(data);
          setOnFreezeForm(true);
          localStorage.setItem('freezeFormSubmitted', 'true');
        }}
        validationSchema={freezeFormSchema}
      >
        {({ handleSubmit, handleChange, values, errors, touched }) => {
          return (
            <form
              className={`${styles.body} ${isMobile ? styles.mob : null}`}
              onSubmit={handleSubmit}
            >
              {!isMobile ? <Text className={styles.heading}>Freeze Membership</Text> : ''}
              {!userDetails ? (
                <div className={styles.spinnerContainer}>
                  <CircularProgress size={45} style={{ color: '#fe5000' }} />
                </div>
              ) : (
                <div className={styles.outerContainer}>
                  <div className={styles.innerContainer}>
                    <div className={styles.card}>
                      <div className={styles.details}>
                        <div className={styles.detailsText}>
                          <p className={styles.key}>Full Name</p>
                          <p className={styles.value}>{userDetails?.name}</p>
                        </div>
                        <div className={styles.detailsText} style={{ marginTop: '10px' }}>
                          <p className={styles.key}>Phone Number</p>
                          <p className={styles.value}>{userPhoneNumber}</p>
                        </div>
                        <div className={styles.detailsText} style={{ marginTop: '10px' }}>
                          <p className={styles.key}>Email</p>
                          <p className={styles.value}>{userDetails?.email}</p>
                        </div>
                      </div>
                      <div>
                        <img
                          src={infoIcon}
                          alt="icon"
                          className={styles.logo}
                          onClick={handleInfoClick}
                        />
                      </div>
                    </div>
                    <div className={styles.fieldContainer}>
                      <div
                        className={`${styles.inputWrapper} ${
                          isMobile ? styles.mobile : ''
                        }`}
                      >
                        <p className={styles.label}>
                          If the email shown above is incorrect,{isMobile ? <br /> : ''}{' '}
                          please provide a new email{' '}
                          <span className={styles.optional}>(optional)</span>.
                        </p>
                        <Textfield
                          value={values.email}
                          onChange={handleChange}
                          name="email"
                          id="email"
                          placeholder="i.e. john@gmail.com"
                          width={inputWidth}
                          className={''}
                          invalid={touched.email && Boolean(errors.email)}
                          error={errors.email}
                          boxShadow={boxShadow}
                        />
                      </div>
                      <div
                        className={`${styles.inputWrapper} ${
                          isMobile ? styles.mobile : ''
                        }`}
                      >
                        <p className={styles.label}>Freeze Start Date*</p>
                        <SingleDateSelection
                          freezeDate={freezeDate}
                          setFreezeDate={setFreezeDate}
                        />
                      </div>
                      <div
                        className={`${styles.inputWrapper} ${
                          isMobile ? styles.mobile : ''
                        }`}
                        style={{ marginTop: '20px' }}
                      >
                        <p className={styles.label}>Duration*</p>
                        <CustomDropdown
                          height={'50px'}
                          backgroundColor={'white'}
                          borderRadius={'10px'}
                          marginTop={'5px'}
                          placeholder="Please Select"
                          name="duration"
                          id="duration"
                          className={''}
                          width={inputWidth}
                          hideIndicatorSeparator
                          options={durationOptions}
                          onChange={e => {
                            handleChange('duration')(e.value);
                            handleDuration(e.label);
                          }}
                          value={values.duration}
                          boxShadow={boxShadow}
                        />
                      </div>
                      <div
                        className={`${styles.inputWrapper} ${
                          isMobile ? styles.mobile : ''
                        }`}
                      >
                        <p className={styles.label} style={{ marginTop: '15px' }}>
                          {!isMobile ? 'Notes or Special Requests*' : 'Message*'}
                        </p>
                        <TextArea
                          value={values.notes}
                          onChange={e => {
                            handleChange('notes')(e.target.value);
                            handleAddNotes(e.target.value);
                          }}
                          name="notes"
                          id="notes"
                          rows={!isMobile ? 6 : 4}
                          width={inputWidth}
                          className={''}
                          boxShadow={boxShadow}
                        />
                      </div>
                      {agreementText && (
                        <div
                          className={styles.conditions}
                          dangerouslySetInnerHTML={{ __html: agreementText }}
                        />
                      )}
                    </div>
                  </div>
                  <div className={isMobile ? styles.buttonContainer : ''}>
                    <button
                      className={
                        !validData || isSubmitFreezeLoading
                          ? styles.disableSubmitButton
                          : styles.submitButton
                      }
                      disabled={!validData || isSubmitFreezeLoading}
                      type="submit"
                    >
                      {isSubmitFreezeLoading ? (
                        <CircularProgress
                          size={20}
                          style={{ color: '#fe5000', marginTop: '5px' }}
                        />
                      ) : (
                        'Submit'
                      )}
                    </button>
                  </div>
                </div>
              )}
              <Dialog
                open={openInfoModal}
                onClose={handleCloseInfoModal}
                classes={{ paper: classes.dialogPaper }}
                disableScrollLock
              >
                <Text className={styles.infoText}>
                  If you would like to change this info, please speak to one of our
                  Customer Service reps.
                </Text>
              </Dialog>
              <Dialog open={openThanksModal} classes={{ paper: classes.dialogPaper }}>
                <div className={styles.iconWrapper}>
                  <img
                    alt=""
                    src={checkmarkCircleOutline}
                    className={`${styles.checkLogo} ${isMobile ? styles.mob : ''}`}
                  />
                  <Text className={styles.thanksText1}>Thank you!</Text>
                  <Text className={styles.thanksText2}>
                    A representative will be in touch soon.
                  </Text>
                  <p onClick={handleGoToBackClick} className={styles.goBackText}>
                    Go Back to Home
                  </p>
                </div>
              </Dialog>
            </form>
          );
        }}
      </Formik>
    </>
  );
};

export default FreezeMembership;
